import React, { useEffect, useState } from "react";
import "./style/finalizeStyle.css";
import "./style/style.scss";
import "../../../style/hovers.scss";
import useFinalizeManager from "./hooks/useFinalizeManager";
import FinalizeWithLogin from "./FinalizeWithLogin";
import FinalizeNoLogin from "./FinalizeNoLogin";
import "./style/style.scss";
import BasketFirstStep from "./BasketFirstStep";
import BasketSecondStep from "./BasketSecondStep";
import useBasketManager from "./hooks/useBasketManager";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../store";
import { useTranslation } from "react-i18next";
import { getUserDataByToken } from "../../store/auth/authActions";
import { GET_USER_DATA_URL } from "../../api/apiRoutes";
import useFacility from "../../../utils/hooks/useFacility";
import useParentMessage from "../../../utils/hooks/useParentMessage";
import useBasketQuestions from "./hooks/useBasketQuestions";
import QuestionsModal from "./QuestionsModal";
import { gaEvent } from "../../../utils/Analitics/ga";
import { GA_VIEW_CART } from "../../../utils/Analitics/gaEventsConsts";
import { canSendToGTM } from "../../../utils/Analitics/analiticsConfigByFacility";
import { sendViewCartEvent } from "../../../utils/Analitics/GtmEvents";

const FinalizePage: React.FC = () => {
  const { scrollToTop } = useParentMessage();
  const {
    getBasket,
    basket,
    deletePromotions,
    total,
    promoCode,
    promoMessage,
    currentPromoCode,
    totalListPrice,
    clearBasket,
  } = useBasketManager();
  const { questions, isModalOpen, setIsModalOpen, setAnswer, handleProceed, onSaveAnswers, loading } =
    useBasketQuestions(basket, getBasket);
  const { t } = useTranslation();
  const userData = useSelector((state: State) => state.auth.userData);
  const { acceptTransaction, alert, onRemoveItemFromBasket, paymentUrl, loader, saveReservation } =
    useFinalizeManager(clearBasket);
  const info = useSelector((state: State) => state.alerts.info);
  const basketLength = useSelector((state: State) => state.basket.basketLength);
  let [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const handleSetStep = (step: number) => {
    if (handleProceed()) {
      setStep(step);
    }
  };

  useEffect(() => {
    scrollToTop();
    getBasket();

    if (!userData) {
      dispatch(getUserDataByToken(GET_USER_DATA_URL(facilityIds)));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  useEffect(() => {
    getBasket();
    deletePromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketLength]);

  const handlePromotion = (promotionCode: string) => {
    promoCode(promotionCode);
  };

  const handleDelete = (index: number) => {
    onRemoveItemFromBasket(index);
  };

  const handleGetBasket = () => {
    setTimeout(() => {
      getBasket();
    }, 500);
  };

  useEffect(() => {
    // Fix GA Event structure for ecommerce tracking
    // Calculate total from basket if needed
    const calculatedTotal = basket.reduce((sum, item) => sum + item.price * item.qnt, 0);
    const eventValue = total > 0 ? total : calculatedTotal;
    gaEvent(
      facilityIds,
      "ecommerce", // Change category to ecommerce
      GA_VIEW_CART,
      {
        event_category: "ecommerce", // Add standard GA4 parameters
        currency: "PLN",
        value: eventValue,
        items: basket.map((item) => ({
          item_id: item.assortIds.replace(/^0,\+/, ""),
          item_name: item.assortName || "Produkt",
          price: item.price,
          quantity: item.qnt,
        })),
      },
      "view_cart" // Add proper label
    );

    // Send GTM view_cart event
    if (canSendToGTM(facilityIds) && basket.length > 0) {
      const gtmEvent = {
        currency: "PLN",
        value: eventValue,
        items: basket.map((item) => ({
          item_id: item.assortIds.replace(/^0,\+/, ""),
          item_name: item.assortName || "Produkt",
          price: item.price,
          quantity: item.qnt,
        })),
      };

      sendViewCartEvent(facilityIds, gtmEvent);
    }

    //eslint-disable-next-line
  }, []);

  // dodaje parametry z HomePage do URL gdu odświeżymy komponent koszyka
  useEffect(() => {
    const loadUrlParams = () => {
      const homePageUrl = sessionStorage.getItem("HomePage");

      if (homePageUrl) {
        const homePageParams = homePageUrl.split("?")[1];
        if (homePageParams) {
          const currentUrl = `${window.location.pathname}?${homePageParams}`;
          window.history.replaceState({}, "", currentUrl);
        }
      }
    };

    loadUrlParams();
  }, []);

  return (
    <React.Fragment>
      <div className="basket-container">
        <div className="basket-steps flex-start-center">
          <div className="active step flex-start-center" onClick={() => setStep(1)}>
            <b className="flex-center-center">1</b>
            <span className="move-up">{t("podsumowanie_koszyka")}</span>
          </div>

          <div
            onClick={() => {
              if (step === 2 || step === 3) {
                setStep(2);
              }
            }}
            className={
              step === 2 || step === 3 || step === 4 ? "active step flex-start-center" : "step flex-start-center"
            }
          >
            <b className="flex-center-center">2</b>
            <span className="move-up">{t("dane_kupujacego")}</span>
          </div>

          <div className={step === 3 || step === 4 ? "active step flex-start-center" : "step flex-start-center"}>
            <b className="flex-center-center">3</b>
            <span className="move-up">{t("platnosc")}</span>
          </div>
        </div>

        {step === 1 && (
          <BasketFirstStep
            promoMessage={promoMessage}
            handlePromotion={handlePromotion}
            handleGetBasket={handleGetBasket}
            handleDelete={handleDelete}
            setStep={handleSetStep}
            basket={basket}
            total={total}
            primaryPrice={totalListPrice}
          />
        )}
        {step === 2 && <BasketSecondStep setStep={handleSetStep} />}
        {step === 3 && (
          <FinalizeNoLogin
            alert={alert}
            onPayTransaction={acceptTransaction}
            onSaveReservation={saveReservation}
            total={total}
            paymentUrl={paymentUrl}
            loader={loader}
            basket={basket}
            promoMessage={promoMessage}
            currentPromoCode={currentPromoCode}
          />
        )}
        {step === 4 && (
          <FinalizeWithLogin
            alertPayment={alert}
            onPayTransaction={acceptTransaction}
            onSaveReservation={saveReservation}
            total={total}
            paymentUrl={paymentUrl}
            loader={loader}
            basket={basket}
            promoMessage={promoMessage}
            currentPromoCode={currentPromoCode}
          />
        )}
      </div>

      {isModalOpen && (
        <QuestionsModal
          questions={questions}
          onClose={() => setIsModalOpen(false)}
          onAnswer={setAnswer}
          onSaveAnswers={onSaveAnswers}
          loading={loading}
        />
      )}
    </React.Fragment>
  );
};

export default FinalizePage;
